import {capitalize} from "lodash";

import {AuthorType} from "@atg-tillsammans-shared/chat-types";

import type {Author, Message, ChannelId} from "@atg-tillsammans-shared/chat-types";

const systemAuthorTypes = [AuthorType.SYSTEM, AuthorType.INFO];

export const isSystemAuthorType = (messageAuthorType: AuthorType): boolean =>
    systemAuthorTypes.includes(messageAuthorType);

export const channelIdToString = (channelId: ChannelId): string => channelId;

export const channelIdFromString = (channelId: string): ChannelId =>
    channelId as ChannelId;

const URL_PATTERN = /https?:\/\/[^\s]+/g;
const ATG_DOMAIN_PATTERN = /^(https?:\/\/)?(localhost|([a-zA-Z0-9-]+\.)*atg\.se)(\/|$)/i;

export const parseUrl = (url: string): URL | null => {
    try {
        return new URL(url);
    } catch (e: unknown) {
        // If the word is not a valid URL, it will throw an error which we ignore
    }
    return null;
};

export const findUrlsInText = (text: string): Array<URL> => {
    const urlMatch = text.match(URL_PATTERN);
    const urls: URL[] = [];
    if (urlMatch) {
        urlMatch
            .map(parseUrl)
            .filter((url) => url !== null)
            .forEach((url) => {
                urls.push(url);
            });
    }
    return urls;
};

export const isAtgDomain = (url: string | URL): boolean => {
    if (typeof url === "string") {
        const newUrl = parseUrl(url);
        return newUrl ? ATG_DOMAIN_PATTERN.test(newUrl.origin) : false;
    }
    return ATG_DOMAIN_PATTERN.test(url.origin);
};

export const filterAtgDomains = (urls: URL | Array<URL> | null): Array<URL> =>
    [urls].flat().filter((url): url is URL => url !== null && isAtgDomain(url));

export const renderMessageTextWithLinks = <T>(
    text: string,
    resolver: (url: URL, index?: number) => T,
): string | Array<string | T> => {
    const urlMatch = findUrlsInText(text);
    if (urlMatch.length > 0) {
        const parts = text.split(URL_PATTERN);
        return parts.reduce<Array<string | T>>((acc, part, index) => {
            acc.push(part);
            if (index < urlMatch.length) {
                acc.push(resolver(urlMatch[index], index));
            }
            return acc;
        }, []);
    }
    return text;
};

export const getDisplayName = (author: Author): string => {
    if (author.firstName && author.lastName) {
        return `${author.firstName} ${author.lastName}`;
    }

    if (author.lastName) return author.lastName;

    if (author.firstName) return author.firstName;

    if (author.displayName) return author.displayName;

    return "";
};

export const getDisplayNameFromMessage = (message: Message): string => {
    if (!message) return "";

    const {user} = message;

    if (!user) return "";

    if (user.firstName && isSystemAuthorType(message.authorType)) {
        return user.firstName;
    }

    return getDisplayName(user);
};

export const capitalizedNameFromMessage = (message?: Message): string =>
    message
        ? getDisplayNameFromMessage(message).split(" ").map(capitalize).join(" ") ?? ""
        : "";
